import { useEffect, useMemo, useState } from "react";
import { Building2, Users2, Target, TrendingUp, Trophy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
  Cell,
} from "recharts";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import StatCard from "@/components/StatCard";
import AlertasMeta from "@/components/AlertasMeta";
import AlertasInfraestrutura from "@/components/AlertasInfraestrutura";
import AnaliseConversaoCard from "@/components/AnaliseConversaoCard";
import TopVendedoresSLACard from "@/components/TopVendedoresSLACard";
import TopCondominiosMesCard from "@/components/TopCondominiosMesCard";
import AlertaRitmoCondoCard from "@/components/AlertaRitmoCondoCard";
import { listCondominios, listFunil, listProspectos } from "@/lib/api";
import { fmtInt, fmtPct, pct } from "@/lib/format";
import { condominiosEmAlerta } from "@/lib/alerts";

export default function ExecutivoTab({ onNavigateCondominios }) {
  const [condos, setCondos] = useState([]);
  const [funil, setFunil] = useState([]);
  const [prospectos, setProspectos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, f, p] = await Promise.all([listCondominios(), listFunil(), listProspectos()]);
        setCondos(c); setFunil(f); setProspectos(p);
      } catch {
        toast.error("Erro ao carregar dados executivos");
      } finally { setLoading(false); }
    })();
  }, []);

  const kpis = useMemo(() => {
    const totalMeta = condos.reduce((s, c) => s + Number(c.meta_vendas || 0), 0);
    const totalVendas = condos.reduce((s, c) => s + Number(c.vendas_realizadas || 0), 0);
    const totalApt = condos.reduce((s, c) => s + Number(c.qtd_apartamentos || 0), 0);
    const totalPropostas = funil.reduce((s, f) => s + Number(f.propostas_enviadas || 0), 0);
    const totalVendasFunil = funil.reduce((s, f) => s + Number(f.vendas_fechadas || 0), 0);
    const totalMetaFunil = funil.reduce((s, f) => s + Number(f.meta_semana || 0), 0);
    return {
      totalCondos: condos.length,
      totalApt,
      atingimentoGlobal: pct(totalVendas, totalMeta),
      atingimentoFunil: pct(totalVendasFunil, totalMetaFunil),
      totalPropostas,
      conversaoFunil: pct(totalVendasFunil, totalPropostas),
    };
  }, [condos, funil]);

  const alertas = useMemo(() => condominiosEmAlerta(condos), [condos]);

  const chartFunil = useMemo(() =>
    [...funil]
      .sort((a, b) => a.semana.localeCompare(b.semana))
      .map((i) => ({
        semana: i.semana.replace(/Semana /, "S").replace(/ - .*/, ""),
        Meta: Number(i.meta_semana || 0),
        Vendas: Number(i.vendas_fechadas || 0),
      })),
  [funil]);

  const rankingCondos = useMemo(() => {
    return [...condos]
      .filter((c) => Number(c.meta_vendas || 0) > 0)
      .map((c) => ({ ...c, _p: pct(c.vendas_realizadas, c.meta_vendas) }))
      .sort((a, b) => b._p - a._p)
      .slice(0, 5);
  }, [condos]);

  const rankingVendedores = useMemo(() => {
    const map = new Map();
    for (const c of condos) {
      // Consultor comercial do condomínio (vendedor associado); "responsavel"
      // é o contato local (síndico/zelador) e não deve entrar no ranking.
      const key = (c.consultor || "").trim() || "Sem consultor";
      const acc = map.get(key) || { vendas: 0, meta: 0, condos: 0 };
      acc.vendas += Number(c.vendas_realizadas || 0);
      acc.meta += Number(c.meta_vendas || 0);
      acc.condos += 1;
      map.set(key, acc);
    }
    return [...map.entries()]
      .map(([nome, v]) => ({ nome, ...v, _p: pct(v.vendas, v.meta) }))
      .sort((a, b) => b._p - a._p);
  }, [condos]);

  if (loading) {
    return <div className="text-center text-muted-foreground py-16">Carregando painel executivo…</div>;
  }

  return (
    <div className="space-y-6">
      {/* KPIs top */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard testId="exec-kpi-meta-global" label="Meta global" value={fmtPct(kpis.atingimentoGlobal)}
          hint={`${fmtInt(kpis.totalCondos)} condomínios · ${fmtInt(kpis.totalApt)} apts`}
          icon={Target}
          tone={kpis.atingimentoGlobal >= 100 ? "success" : kpis.atingimentoGlobal >= 50 ? "warning" : "danger"} />
        <StatCard testId="exec-kpi-funil-semanal" label="Meta semanal acumulada" value={fmtPct(kpis.atingimentoFunil)}
          hint={`${fmtInt(kpis.totalPropostas)} propostas enviadas`}
          icon={TrendingUp}
          tone={kpis.atingimentoFunil >= 100 ? "success" : kpis.atingimentoFunil >= 50 ? "warning" : "danger"} />
        <StatCard testId="exec-kpi-conversao" label="Conversão proposta→venda" value={fmtPct(kpis.conversaoFunil)}
          icon={CheckCircle2} tone="success" />
      </div>

      {/* Alertas */}
      <AlertasMeta items={alertas} onSelect={onNavigateCondominios} />

      {/* Chart + Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
          <div className="mb-4">
            <h3 className="font-heading font-semibold text-lg">Meta vs Vendas por semana</h3>
            <p className="text-xs text-muted-foreground">Evolução da vendedora ao longo do mês.</p>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartFunil} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="semana" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Meta" fill="hsl(var(--chart-3))" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Vendas" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5" data-testid="ranking-vendedores">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-lg">Ranking por vendedora</h3>
          </div>
          <div className="space-y-3">
            {rankingVendedores.length === 0 && (
              <div className="text-sm text-muted-foreground">Sem dados.</div>
            )}
            {rankingVendedores.map((v, idx) => {
              const p = v._p;
              const tone = p >= 100 ? "text-emerald-500" : p >= 50 ? "text-amber-500" : "text-red-500";
              return (
                <div key={v.nome} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] grid place-items-center font-semibold">
                        {idx + 1}
                      </span>
                      <span className="truncate">{v.nome}</span>
                    </div>
                    <span className={`text-xs font-semibold tabular-nums ${tone}`}>{fmtPct(p)}</span>
                  </div>
                  <Progress value={Math.min(p, 100)} className="h-1.5" />
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    {fmtInt(v.vendas)}/{fmtInt(v.meta)} · {v.condos} condomínio(s)
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top condomínios */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="top-condominios">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-lg">Top 5 condomínios</h3>
          </div>
          <Badge variant="outline">Maior % atingido</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {rankingCondos.length === 0 && (
            <div className="text-sm text-muted-foreground">Sem dados.</div>
          )}
          {rankingCondos.map((c) => {
            const p = c._p;
            const tone = p >= 100 ? "text-emerald-500" : p >= 50 ? "text-amber-500" : "text-red-500";
            return (
              <div key={c.id} className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground truncate">{c.consultor || "—"}</div>
                <div className="text-sm font-medium truncate mt-0.5">{c.condominio}</div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className={`font-bold tabular-nums ${tone}`}>{fmtPct(p)}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {fmtInt(c.vendas_realizadas)}/{fmtInt(c.meta_vendas)}
                  </span>
                </div>
                <Progress value={Math.min(p, 100)} className="h-1.5 mt-2" />
              </div>
            );
          })}
        </div>
      </div>
      {/* Análise de conversão de prospectos */}
      <AnaliseConversaoCard prospectos={prospectos} />

      {/* Top vendedoras por tempo de resposta ao cliente (Onboarding SLA) */}
      <TopVendedoresSLACard />

      {/* Pódio dos prédios mais quentes do mês + Alerta de ritmo */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <TopCondominiosMesCard onNavigate={onNavigateCondominios} />
        <AlertaRitmoCondoCard onNavigate={onNavigateCondominios} />
      </div>

      {/* Alertas de infraestrutura (prospectos de condomínios) */}
      <AlertasInfraestrutura condominios={condos} />
    </div>
  );
}
